'use client';

import {
  ARIA_LABEL_CATEGORIES_LIST,
  ARIA_LABEL_CATEGORIES_LIST_LONG,
  ARIA_LABEL_SUB_NAV,
  ARIA_LABEL_TOP_ARTICLES,
  URL_PAGE_ALWAYS,
} from '@/shared/constants';
import React, { useEffect, useState } from 'react';
import { ArticleCard as ArticleCardType } from '@/sanity/fetchArticles';
import { CategoryLink } from '@/sanity/fetchCategories';
import NavigationCategoriesList from './NavigationCategoriesList';
import ArticlesList from './ArticlesList';
import { useBreakpoints } from '@/utils/useBreakpoints';
import styles from './NavigationSubMenu.module.scss';

function IconCaret({ rotate }: { rotate?: boolean }) {
  return (
    <svg
      className={[styles.caret, rotate ? styles.rotated : ''].filter(Boolean).join(' ')}
      width="8"
      height="4"
      viewBox="0 0 8 4"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M0.25 0.125L4 3.875L7.75 0.125H0.25Z" fill="#1D1B20" />
    </svg>
  );
}

interface NavigationSubMenuProps {
  title: string;
  articles?: ArticleCardType[];
  categories?: CategoryLink[];
}

const NavigationSubMenu: React.FC<NavigationSubMenuProps> = ({
  title,
  articles,
  categories,
}) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const id = 'sub-menu';
  const { isLg } = useBreakpoints();

  useEffect(() => {
    const removeListener = () => {
      document.body.classList.remove('submenu-overlay');
      document.body.removeEventListener('click', (e) => {
        if (e.target === document.body) setIsSubMenuOpen(false);
      });
    };

    if (isSubMenuOpen && isLg) {
      document.body.classList.add('submenu-overlay');
      document.body.addEventListener('click', (e) => {
        if (e.target === document.body) setIsSubMenuOpen(false);
      });
    } else if (!isSubMenuOpen || !isLg) {
      removeListener();
    }

    return () => {
      removeListener();
    };
  }, [isSubMenuOpen, isLg]);

  return (
    <>
      <li className={styles.menuItem}>
        <a href={URL_PAGE_ALWAYS} className={styles.navLink}>
          {title}
        </a>
      </li>

      <li className={styles.menuItem}>
        <button
          onClick={() => setIsSubMenuOpen(!isSubMenuOpen)}
          className={[styles.dropdownButton, isSubMenuOpen ? styles.open : '']
            .filter(Boolean)
            .join(' ')}
          aria-expanded={isSubMenuOpen}
          aria-controls={`#${id}`}
          aria-haspopup="true"
        >
          {ARIA_LABEL_CATEGORIES_LIST}
          <IconCaret rotate={isSubMenuOpen} />
        </button>
        <div
          aria-hidden={isSubMenuOpen}
          aria-label={ARIA_LABEL_SUB_NAV}
          id={id}
          className={[styles.subMenuPanel, isSubMenuOpen ? styles.open : '']
            .filter(Boolean)
            .join(' ')}
        >
          <div className={styles.subMenuContent}>
            <div className={styles.categoriesColumn}>
              <div
                id={ARIA_LABEL_CATEGORIES_LIST_LONG}
                className={styles.categoriesLabel}
              >
                {ARIA_LABEL_CATEGORIES_LIST_LONG}
              </div>
              {categories && categories.length && (
                <NavigationCategoriesList
                  labelledBy={ARIA_LABEL_CATEGORIES_LIST_LONG}
                  categories={categories}
                  isSubMenuOpen={isSubMenuOpen}
                  onCategoryClick={() => setIsSubMenuOpen(false)}
                />
              )}
            </div>
            {articles && articles.length && (
              <ArticlesList
                label={ARIA_LABEL_TOP_ARTICLES}
                articles={articles}
                isSubMenuOpen={isSubMenuOpen}
                onArticleClick={() => setIsSubMenuOpen(false)}
              />
            )}
          </div>
        </div>
      </li>
    </>
  );
};

export default NavigationSubMenu;
